import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { roadmapAreas } from '../data/research';
import { easeOutStrong, fadeUp } from '../utils/motion';
export function RoadmapPage() {
  const scrollToArea = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <>
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-24" data-id="element-1890" data-mp="dhzm8d5ci4ei">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOutStrong
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-1891" data-mp="ifspaxbcqiak">
          <div className="lg:col-span-5" data-id="element-1892" data-mp="2oblvfabzn9fi">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-1893" data-mp="2ocojlahxexrq">Roadmap</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-6xl text-ink-dark leading-[1.05] tracking-tight" data-id="element-1894" data-mp="2t9ngu1wvzku5">
              Where SyNeural’s Research Is Going
            </h1>
          </div>
          <div className="lg:col-span-7 lg:pt-10" data-id="element-1895" data-mp="2c3bmibe5v2pg">
            <p className="text-2xl text-ink-dark leading-relaxed mb-6" data-id="element-1896" data-mp="28286now456o8">
              SyNeural studies how people develop sound judgment, how
              researchers connect ideas, and how institutions turn knowledge
              into responsible action.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1897" data-mp="2vsm09jucjdkx">
              This roadmap carries forward the research agenda behind CJI and
              NCJI while expanding it to include the Semantic Knowledge Platform
              (SKP). Each direction below describes work we intend to explore,
              test, and refine—not a claim that every proposed measure or
              application has already been validated.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-1898" data-mp="1bwetp6g4ejfc">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-1899" data-mp="1bwy1huc23rfn">
          <nav aria-label="Roadmap directions" className="hidden lg:block lg:col-span-4" data-id="element-1900" data-mp="1gtf1pmwj5w93">
            <div className="sticky top-28" data-id="element-1901" data-mp="12vgr8slbyjyv">
              <p className="text-sm font-medium text-ink-muted mb-4" data-id="element-1902" data-mp="17ubimixd5gul">
                {roadmapAreas.length} research directions
              </p>
              <ul className="border-l border-line" data-id="element-1903" data-mp="t4drpv81ud1p">
                {roadmapAreas.map(area => <li key={area.id} data-id="element-1904" data-mp="xy88apscybwk">
                    <a href={`#${area.id}`} onClick={scrollToArea(area.id)} className="block -ml-px border-l-2 border-transparent hover:border-navy pl-4 py-1.5 text-sm text-ink-muted hover:text-navy transition-colors duration-150" data-id="element-1905" data-mp="k58o4ahmrtio">
                      {area.title}
                    </a>
                  </li>)}
              </ul>
            </div>
          </nav>

          <div className="lg:col-span-8" data-id="element-1906" data-mp="3rq04iw6y8ggc">
            {roadmapAreas.map(area => <motion.article key={area.id} id={area.id} {...fadeUp} className="scroll-mt-28 border-t border-line py-12 first:border-t-0 first:pt-0" data-id="element-1907" data-mp="3ms857h3z5s24">
                <h2 className="font-heading font-bold text-3xl text-ink-dark tracking-tight mb-5" data-id="element-1908" data-mp="ltdlzqflaxq">
                  {area.title}
                </h2>
                <p className="text-xl text-ink-dark leading-relaxed mb-5" data-id="element-1909" data-mp="9j3nbgnlos0j">{area.lead}</p>
                <div className="space-y-4" data-id="element-1910" data-mp="o9b29meefswh">
                  {area.paragraphs.map(paragraph => <p key={paragraph} className="text-lg text-ink-muted leading-relaxed" data-id="element-1911" data-mp="jazoj0kzgy8z">
                      {paragraph}
                    </p>)}
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-20 lg:py-28" data-id="element-1912" data-mp="2mrd1rmdvvbfc">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1913" data-mp="2moquzoytgzy6">
          <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight mb-6" data-id="element-1914" data-mp="2rnv28a26knlv">
            From roadmap to responsible application
          </h2>
          <p className="text-xl text-white/80 leading-relaxed mb-10" data-id="element-1915" data-mp="31eppnhr5xt8w">
            SyNeural’s work connects three activities: organizing knowledge,
            developing human capability, and improving institutional decisions.
            SKP helps connect and govern what is known. CJI and NCJI explore how
            people develop judgment. Pilots and independent review test whether
            the resulting tools work as intended.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1916" data-mp="2hpfdueujfsg2">
            <a href="/research" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-1917" data-mp="3p5283mk1b8dq">
              Explore the Research Agenda
              <ArrowRight size={18} data-id="element-1918" data-mp="1dirwzdycv754" />
            </a>
            <a href="/partnerships" className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-1919" data-mp="18qgo3lln0b0f">
              Discuss a Research Partnership
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}